import { getAssetUrl } from '@lighthouse/assets'
import {
    type BlockType,
    CALENDAR_MODE,
    CalendarMode,
    type ChartType,
    type CustomViewLayout,
    DIRECTION,
    type FieldInputType,
    FILE_MODE,
    FileMode,
    FilterBlockMode,
    type QrBarcodeEnum,
    type ViewOptions,
    type ViewType
} from '@lighthouse/core'

import type { MenuItem } from '../components'

export type ListItemAction = 'DELETE' | 'EDIT'

// /////////////////////// block icon start //////////////////////////// //
export const ChartTypeIconMap: Record<ChartType, string> = {
    bar: 'ChartsBlock',
    pie: 'PieChart2Block',
    line: 'DiagramUpBlock',
    funnel: 'ChartFunnel',
    indicator: 'PlateBlock',
    composite: 'ChartCompound',
    striation: 'ChartBarHorizontal',
    radar: 'ChartRadar'
}
export const ChartTypePlaceHolderMap: Record<ChartType, string> = {
    bar: '请在「右侧栏>配置」中选择数据来源、维度和指标', // '建议 任意数量的维度和数值，1个次轴',
    pie: '请在「右侧栏>配置」中选择数据来源、维度和指标', // '建议 1个维度，1个数值或 0个维度，多个数值',
    line: '请在「右侧栏>配置」中选择数据来源、维度和指标', // '建议 任意数量的维度和数值，1个次轴',
    funnel: '请在「右侧栏>配置」中选择数据来源、维度和指标', // '建议 1个维度，1个数值或 0个维度，多个数值',
    indicator: '请在「右侧栏>配置」中选择数据来源、维度', // '需要 1个数值',
    composite: '请在「右侧栏>配置」中选择数据来源、维度和指标', // '建议 任意数量的维度和数值，1个次轴',
    striation: '请在「右侧栏>配置」中选择数据来源、维度和指标', // '建议 1个维度，多个数值'
    radar: '请在「右侧栏>配置」中选择数据来源、维度和指标' // '建议 1个维度，多个数值'
}
export const FIELD_BLOCK_ICON_MAP: Record<FieldInputType, string> = {
    number: 'PropertyNumberBlock',
    file: 'PropertyFileBlock',
    text: 'PropertyTextBlock',
    email: 'PropertyEmailBlock',
    url: 'PropertyLinkBlock',
    person: 'PropertyPersonBlock',
    phoneNumber: 'PropertyPhoneBlock',
    date: 'PropertyCalendarBlock',
    relativeSelect: 'PropertySingleSelectBlock',
    notes: 'PropertyNoteBlock',
    checkbox: 'PropertyCheckboxBlock',
    slider: 'SliderBlock',
    cascade: 'PropertyCascade',
    calendar: 'PropertyCalendar'
}
export const VIEW_BLOCK_ICON_MAP: Record<ViewType, string> = {
    table: 'NewTableBlock',
    advancedTable: 'AdvanceTableLayer',
    list: 'ListBlock',
    gallery: 'GalleryBlock',
    kanban: 'PauseBlock',
    calendar: 'CalendarBlock',
    custom: 'CustomizingViews'
}

export const QR_BARCODE_BLOCK_ICON_MAP: Record<QrBarcodeEnum, string> = {
    QR_CODE: 'QRCode',
    BARCODE: 'Barcode'
}

export const CONTAINER_ICON_MAP = {
    [DIRECTION.horizontal]: 'ColumnContainer',
    [DIRECTION.vertical]: 'RowContainer'
}

export const FILTER_BLOCK_ICON_MAP: Record<FilterBlockMode, string> = {
    [FilterBlockMode.normal]: 'PropertySingleSelect',
    [FilterBlockMode.tile]: 'StackSimple'
}

export const BlockTypeIconMap: Record<Exclude<BlockType, 'container' | 'field' | 'chart' | 'view'>, string> = {
    // prompt: 'BlockCallout',
    // quote: 'BlockQuote',
    image: 'ImageBlock',
    // tag: 'Group',
    // notice: 'MediaSpeakerLow',
    button: 'ButtonBasicBlock',
    card: 'ImageText',
    divider: 'MinusSquareBlock',
    video: 'VideoCameraBlock',
    audio: 'MediaSpeakerLow',
    fieldGroup: 'ThreeSquaresBlock',
    breadcrumb: 'PostsCarouselHorizontalBlock',
    iframe: 'WindowFrameBlock',
    collapse: 'DocumentBlock',
    file: 'FileSendBlock',
    // container: 'LayerTagContainerBlock',
    formContainer: 'LayerFormContainerBlock',
    tabs: 'HorizontalLayoutBlock',
    text: 'LayerTextComponentBlock',
    filter: 'FilterControlBlock',
    qrBarcode: 'QRCode',
    subForm: 'LayerEditPage',
    icon: 'IconBlock',
    userCenter: 'PersonalCenterBlock',
    floatBox: 'Copy',
    onOff: 'Switch',
    toc: 'ContentOutlineBlock'
}
// /////////////////////// block icon end //////////////////////////// //

// /////////////////////// block 名称 start //////////////////////////// //
export const CHART_BLOCK_NAME_MAP: Record<ChartType, string> = {
    bar: '柱状图',
    line: '折线图',
    composite: '复合图表',
    pie: '饼图',
    funnel: '漏斗图',
    indicator: '指标卡',
    striation: '条形图',
    radar: '雷达图'
}
export const FIELD_BLOCK_NAME_MAP: Record<FieldInputType, string> = {
    number: '数值',
    file: '附件',
    text: '文本',
    email: '邮箱',
    url: '链接',
    person: '人员',
    phoneNumber: '电话',
    date: '日期',
    relativeSelect: '选择器',
    notes: '笔记',
    checkbox: '复选框',
    slider: '滑动输入条',
    cascade: '级联',
    calendar: '日历'
}

export const BLOCK_NAME_MAP: Record<Exclude<BlockType, 'field' | 'chart' | 'view'>, string> = {
    breadcrumb: '面包屑',
    button: '按钮',
    card: '图文导航',
    collapse: '折叠面板',
    container: '空白容器',
    divider: '分割线',
    fieldGroup: '字段组',
    file: '文件预览',
    formContainer: '表单容器',
    iframe: '嵌入网页',
    image: '图片',
    tabs: '标签栏',
    filter: '筛选控制器',
    text: '文本',
    video: '视频',
    audio: '音频',
    qrBarcode: '条码',
    subForm: '子表单',
    icon: '图标',
    userCenter: '个人中心',
    floatBox: '弹出窗口',
    onOff: '开关按钮',
    toc: '笔记目录'
}
// /////////////////////// block 名称 end //////////////////////////// //

export const ChartBlockThumbImageMap: Record<ChartType, string> = {
    bar: getAssetUrl('blocks', 'bar_chart.svg'),
    pie: getAssetUrl('blocks', 'pie_chart.svg'),
    line: getAssetUrl('blocks', 'line_chart.svg'),
    indicator: getAssetUrl('blocks', 'indicator_card.svg'),
    funnel: getAssetUrl('blocks', 'funnel_card.svg'),
    composite: getAssetUrl('blocks', 'composite_chart.svg'),
    striation: getAssetUrl('blocks', 'striation_chart.svg'),
    radar: getAssetUrl('blocks', 'radar_chart.svg')
}

export const BlockThumbImageMap: Record<Exclude<BlockType, 'field' | 'chart'> | ViewOptions['viewType'] | CustomViewLayout, string> = {
    view: getAssetUrl('blocks', 'board.svg'),
    table: getAssetUrl('blocks', 'table.svg'),
    advancedTable: getAssetUrl('blocks', 'advanced_table.svg'),
    list: getAssetUrl('blocks', 'list.svg'),
    gallery: getAssetUrl('blocks', 'gallery.svg'),
    image: getAssetUrl('blocks', 'image.svg'),
    button: getAssetUrl('blocks', 'button.svg'),
    card: getAssetUrl('blocks', 'card.svg'),
    divider: getAssetUrl('blocks', 'divider.svg'),
    kanban: getAssetUrl('blocks', 'kanban.svg'),
    video: getAssetUrl('blocks', 'video.svg'),
    audio: getAssetUrl('blocks', 'audio.svg'),
    fieldGroup: getAssetUrl('blocks', 'field_group.svg'),
    calendar: getAssetUrl('blocks', 'calendar.svg'),
    breadcrumb: getAssetUrl('blocks', 'breadcrumb.svg'),
    iframe: getAssetUrl('blocks', 'iframe.svg'),
    collapse: getAssetUrl('blocks', 'collapse.svg'),
    file: getAssetUrl('blocks', 'file.svg'),
    tabs: getAssetUrl('blocks', 'tabs.svg'),
    container: getAssetUrl('blocks', 'empty_module.svg'),
    formContainer: getAssetUrl('blocks', 'form_container.svg'),
    text: getAssetUrl('blocks', 'text.svg'),
    custom: getAssetUrl('blocks', 'custom_view.svg'),
    waterfall: getAssetUrl('blocks', 'custom_view_waterfall.svg'),
    grid: getAssetUrl('blocks', 'custom_view_grid.svg'),
    carousel: getAssetUrl('blocks', 'custom_view_carousel.svg'),
    comment: getAssetUrl('blocks', 'custom_view_comment.svg'),
    weekCalendar: getAssetUrl('blocks', 'custom_view_week_calendar.svg'),
    monthCalendar: getAssetUrl('blocks', 'custom_view_week_calendar.svg'),
    tree: getAssetUrl('blocks', 'custom_view_tree.svg'),
    multiDimensional: getAssetUrl('blocks', 'custom_view_multi_dimensional.svg'),
    filter: getAssetUrl('blocks', 'filter.svg'),
    qrBarcode: getAssetUrl('blocks', 'qr_barcode.svg'),
    subForm: getAssetUrl('blocks', 'sub_form.svg'),
    icon: getAssetUrl('blocks', 'icon.svg'),
    userCenter: getAssetUrl('blocks', 'userCenter.svg'),
    floatBox: getAssetUrl('blocks', 'float.svg'),
    onOff: getAssetUrl('blocks', 'switch_button.svg'),
    toc: getAssetUrl('blocks', 'text.svg')
}

export const FieldBlockThumbImageMap: Record<FieldInputType, string> = {
    text: getAssetUrl('blocks', 'text_input.svg'),
    number: getAssetUrl('blocks', 'number_input.svg'),
    email: getAssetUrl('blocks', 'email_input.svg'),
    url: getAssetUrl('blocks', 'url_input.svg'),
    file: getAssetUrl('blocks', 'file_input.svg'),
    relativeSelect: getAssetUrl('blocks', 'relative_select_input.svg'),
    person: getAssetUrl('blocks', 'person_input.svg'),
    phoneNumber: getAssetUrl('blocks', 'phone_number_input.svg'),
    date: getAssetUrl('blocks', 'date_input.svg'),
    notes: getAssetUrl('blocks', 'notes_input.svg'),
    checkbox: getAssetUrl('blocks', 'checkbox_input.svg'),
    slider: getAssetUrl('blocks', 'slider.svg'),
    cascade: getAssetUrl('blocks', 'cascade_input.svg'),
    calendar: getAssetUrl('blocks', 'calendar_input.svg')
}

export const FileFieldBlockThumbImageMap: Record<FileMode, string> = {
    [FILE_MODE.list]: getAssetUrl('blocks', 'file_list.svg'),
    [FILE_MODE.thumbnail]: getAssetUrl('blocks', 'file_thumbnail.svg'),
    [FILE_MODE.grid]: getAssetUrl('blocks', 'file_grid.svg')
}

export const CalendarFieldBlockThumbImageMap: Record<CalendarMode, string> = {
    [CALENDAR_MODE.week]: getAssetUrl('blocks', 'calendar_week.svg'),
    [CALENDAR_MODE.month]: getAssetUrl('blocks', 'calendar_month.svg')
}

export const menuOperationItems: MenuItem<ListItemAction>[] = [
    {
        section: '操作',
        items: [
            {
                name: '编辑',
                icon: 'PencilSimple',
                hotkey: '',
                action: 'EDIT'
            },
            {
                name: '删除',
                icon: 'Trash',
                hotkey: '',
                action: 'DELETE'
            }
        ]
    }
]
